import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";

export interface PaginaEvent {
  paginaActual: number;
  elementosPorPagina: number;
  inicioRegistro: number;
  finRegistro: number;
}

@Component({
  selector: "app-paginacion",
  templateUrl: "./paginacion.component.html",
  styleUrls: ["./paginacion.component.css"],
})
export class PaginacionComponent implements OnChanges {
  // ===========================
  // ENTRADAS (INPUT)
  // ===========================
  @Input() totalRegistros = 0;
  @Input() elementosPorPagina = 5;
  @Input() opcionesElementosPorPagina: number[] = [5, 10, 20, 50];
  @Input() paginaActual = 1; //  NUEVO: ahora la controla el padre

  // Cantidad máxima de botones de página visibles en mobile
  @Input() maxVisiblesMobile = 4;
  // Ancho (px) a partir del cual se considera "mobile"
  @Input() breakpointMobile = 768;

  // ===========================
  // SALIDAS (OUTPUT)
  // ===========================
  @Output() paginaCambiada = new EventEmitter<PaginaEvent>();
  @Output() paginaActualChange = new EventEmitter<number>(); //  NUEVO: habilita [(paginaActual)]

  // ===========================
  // ESTADO INTERNO - PAGINACIÓN
  // ===========================
  totalPaginas = 1;
  paginas: (number | string)[] = [];
  inicioRegistro = 0;
  finRegistro = 0;

  // Detección de mobile
  esMobile = window.innerWidth <= this.breakpointMobile;

  // =====================================================
  // ESTADO INTERNO - DROPDOWN "MOSTRAR X REGISTROS"
  // =====================================================
  dropdownAbierto = false;

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Ya NO forzamos paginaActual = 1 aquí.
    // actualizarPaginacion() ya corrige la página si quedó fuera de rango.
    this.actualizarPaginacion();
  }

  @HostListener("window:resize")
  onResize(): void {
    const nuevoEsMobile = window.innerWidth <= this.breakpointMobile;
    if (nuevoEsMobile !== this.esMobile) {
      this.esMobile = nuevoEsMobile;
      this.actualizarPaginacion();
    }
  }

  actualizarPaginacion(): void {
    this.totalPaginas = Math.max(
      1,
      Math.ceil(this.totalRegistros / this.elementosPorPagina),
    );

    if (this.paginaActual > this.totalPaginas) {
      this.paginaActual = this.totalPaginas;
    }
    if (this.paginaActual < 1) {
      this.paginaActual = 1;
    }

    this.paginas = this.calcularVentanaPaginas();

    const inicio = (this.paginaActual - 1) * this.elementosPorPagina;
    const fin = inicio + this.elementosPorPagina;

    this.inicioRegistro = this.totalRegistros === 0 ? 0 : inicio + 1;
    this.finRegistro = Math.min(fin, this.totalRegistros);

    this.emitirCambio();
  }

  // Genera algo como: 1 ... 4 5 [6] 7 8 ... 20 (desktop)
  // o una ventana simple de N botones sin puntos suspensivos (mobile)
  private calcularVentanaPaginas(): (number | string)[] {
    const total = this.totalPaginas;
    const actual = this.paginaActual;

    if (this.esMobile) {
      return this.calcularVentanaMobile(total, actual, this.maxVisiblesMobile);
    }

    const delta = 1;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const paginas: (number | string)[] = [1];

    const inicioRango = Math.max(2, actual - delta);
    const finRango = Math.min(total - 1, actual + delta);

    if (inicioRango > 2) {
      paginas.push("...");
    }

    for (let i = inicioRango; i <= finRango; i++) {
      paginas.push(i);
    }

    if (finRango < total - 1) {
      paginas.push("...");
    }

    paginas.push(total);

    return paginas;
  }

  // Ventana deslizante simple para mobile: sin "...", máximo `maxVisibles` botones,
  // centrada en la página actual y ajustada si toca un borde.
  private calcularVentanaMobile(
    total: number,
    actual: number,
    maxVisibles: number,
  ): number[] {
    if (total <= maxVisibles) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    let inicio = Math.max(1, actual - Math.floor((maxVisibles - 1) / 2));
    let fin = inicio + maxVisibles - 1;

    if (fin > total) {
      fin = total;
      inicio = fin - maxVisibles + 1;
    }

    return Array.from({ length: fin - inicio + 1 }, (_, i) => inicio + i);
  }

  // Cambia a la página indicada si es válida
  irPagina(pagina: number | string): void {
    if (typeof pagina !== "number" || pagina < 1 || pagina > this.totalPaginas) {
      return;
    }

    this.paginaActual = pagina;
    this.paginaActualChange.emit(this.paginaActual); // 👈 sincroniza con el padre
    this.actualizarPaginacion();
  }

  // Retroceder a la página anterior si no es la primera
  anterior(): void {
    if (this.paginaActual > 1) {
      this.paginaActual--;
      this.paginaActualChange.emit(this.paginaActual); // 👈 sincroniza con el padre
      this.actualizarPaginacion();
    }
  }

  // Avanzar a la siguiente página si no es la última
  siguiente(): void {
    if (this.paginaActual < this.totalPaginas) {
      this.paginaActual++;
      this.paginaActualChange.emit(this.paginaActual); // 👈 sincroniza con el padre
      this.actualizarPaginacion();
    }
  }

  // ===========================================
  // DROPDOWN PERSONALIZADO "MOSTRAR X REGISTROS"
  // ===========================================

  toggleDropdown(): void {
    this.dropdownAbierto = !this.dropdownAbierto;
  }

  seleccionarElementosPorPagina(opcion: number): void {
    this.elementosPorPagina = opcion;
    this.dropdownAbierto = false;
    this.paginaActual = 1;
    this.paginaActualChange.emit(this.paginaActual); // 👈 sincroniza con el padre
    this.actualizarPaginacion();
  }

  // Cierra el dropdown si el usuario hace click fuera del componente
  @HostListener("document:click", ["$event"])
  clickFuera(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.dropdownAbierto = false;
    }
  }

  /** Empaqueta y emite el estado actual hacia el componente padre. */
  private emitirCambio(): void {
    this.paginaCambiada.emit({
      paginaActual: this.paginaActual,
      elementosPorPagina: this.elementosPorPagina,
      inicioRegistro: this.inicioRegistro,
      finRegistro: this.finRegistro,
    });
  }


  get mostrarSelectorElementos(): boolean {
    const minOpcion = Math.min(...this.opcionesElementosPorPagina);
    return this.totalRegistros > minOpcion;
  }
}